import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Alert } from './Alert';
import { BottomSheet, BottomSheetContent } from './BottomSheet';
import { Button } from './Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  type DialogContentProps,
} from './Dialog';
import { FormActions } from './Field';
import { Icon } from './Icon';
import { Grid, Inline, Stack } from './Layout';
import { PlanCard } from './PlanCard';
import { Text } from './Text';

export interface PlanGatePlan {
  id: string;
  name: string;
  /** The price line, already formatted: "$160 / month". */
  price?: React.ReactNode;
  /** What it adds, one line each. */
  features?: React.ReactNode[];
  /** The plan the upgrade goes to: its card is emphasised and tagged. */
  recommended?: boolean;
}

export interface PlanGateNoticeProps {
  /** Why the action is out of reach — "Upgrade your plan to access …". */
  message: React.ReactNode;
  /** A second line: what tripped the gate, or what upgrading unlocks. */
  detail?: React.ReactNode;
  /** A failure from the primary action, drawn as a destructive alert. */
  error?: React.ReactNode;
  /**
   * The way forward — "View Plans", "Upgrade Plan". Leave both out when the
   * viewer cannot take it: billing belongs to whoever manages the
   * subscription, and a button that leads to a refusal is worse than a
   * message saying who to ask.
   */
  primaryLabel?: React.ReactNode;
  onPrimary?: () => void;
  dismissLabel?: React.ReactNode;
  onDismiss: () => void;
  /** The primary action is in flight: a spinner on it, and nothing clickable. */
  busy?: boolean;
  /**
   * The plans that include what was asked for, as cards above the buttons:
   * the upsell. Shown only to a viewer who can upgrade.
   */
  plans?: PlanGatePlan[];
  /**
   * Whether this viewer can change the plan — `subscription.view`. When
   * false there are no plans and no way forward, only the reason and a line
   * saying who to ask. Left out, the gate goes by whether `onPrimary` was
   * given and adds no line, for a caller that words the reason itself.
   */
  canUpgrade?: boolean;
  /** What a viewer who cannot upgrade is told to do. */
  askOwnerLabel?: React.ReactNode;
}

/**
 * The inside of a "you need a higher plan" dialog: the reason, an optional
 * second line, and the two ways out.
 *
 *   <PlanGateNotice
 *     message="Upgrade your plan to access create call."
 *     detail="You have reached your monthly call limit."
 *     primaryLabel="View Plans"
 *     onPrimary={goToBilling}
 *     onDismiss={close}
 *   />
 *
 * Without `onPrimary` it is the reason and Close, for a viewer who cannot
 * change the plan themselves.
 *
 * The two upsells from the plan matrix:
 *
 *   // A member with subscription.view: the plans, Upgrade and No thanks.
 *   <PlanGateNotice message="Video is on Pro and Enterprise." plans={plans}
 *     primaryLabel="Upgrade" onPrimary={upgrade}
 *     dismissLabel="No thanks" onDismiss={close} />
 *
 *   // Everyone else: the reason and who to ask.
 *   <PlanGateNotice message="Video is on Pro." canUpgrade={false} onDismiss={close} />
 *
 * On its own for a dialog that opens on a form and then finds the plan will
 * not allow it — the header is already there, only the body changes. For a
 * dialog whose only job is the gate, `PlanGate` wraps this in the shell.
 */
export function PlanGateNotice({
  message,
  detail,
  error,
  primaryLabel,
  onPrimary,
  dismissLabel = 'Close',
  onDismiss,
  busy = false,
  plans,
  canUpgrade,
  askOwnerLabel = 'Ask your owner to upgrade.',
}: PlanGateNoticeProps) {
  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <Text variant="body-sm" tone="muted">
          {message}
        </Text>
        {detail ? (
          <Text variant="body-sm" tone="muted">
            {detail}
          </Text>
        ) : null}
        {canUpgrade === false ? (
          <Text variant="body-sm-strong">{askOwnerLabel}</Text>
        ) : null}
      </Stack>
      {canUpgrade !== false && plans?.length ? (
        <Stack gap={3}>
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              title={plan.name}
              price={
                plan.price ? (
                  <Text variant="body-strong" as="span" numeric>
                    {plan.price}
                  </Text>
                ) : undefined
              }
              features={plan.features}
              featuresTitle="Adds:"
              emphasis={plan.recommended}
              badge={plan.recommended ? 'Recommended' : undefined}
            />
          ))}
        </Stack>
      ) : null}
      {error ? <Alert tone="destructive">{error}</Alert> : null}
      <FormActions>
        <Button
          type="button"
          variant="outline"
          onClick={onDismiss}
          disabled={busy}
        >
          {dismissLabel}
        </Button>
        {canUpgrade !== false && onPrimary ? (
          <Button type="button" onClick={onPrimary} loading={busy}>
            {primaryLabel}
          </Button>
        ) : null}
      </FormActions>
    </Stack>
  );
}

export interface PlanGateProps extends PlanGateNoticeProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Create Call unavailable", "Upgrade Required". */
  title: React.ReactNode;
  /** A glyph before the title. */
  icon?: LucideIcon;
  size?: DialogContentProps['size'];
  /**
   * `dialog` on a desk. `sheet` on a phone: the same gate rising from the
   * bottom edge, as the video upsell does when a driver picks a video.
   */
  presentation?: 'dialog' | 'sheet';
}

/**
 * A dialog that says the plan does not allow something, and offers the plans.
 *
 *   <PlanGate
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Subscription Required"
 *     message="Please subscribe to Relay Tow to publish your website."
 *     primaryLabel="See Options"
 *     onPrimary={goToBilling}
 *     dismissLabel="Cancel"
 *     onDismiss={() => setOpen(false)}
 *   />
 *
 * Three screens had their own spelling of this — a gate on an action, an
 * upgrade prompt on a feature, a subscription prompt on publishing — and each
 * laid the same four parts out differently. This is the one shape.
 */
export function PlanGate({
  open,
  onOpenChange,
  title,
  icon,
  size = 'sm',
  presentation = 'dialog',
  ...body
}: PlanGateProps) {
  if (presentation === 'sheet') {
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange}>
        <PlanGateSheetContent title={title} {...body} />
      </BottomSheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size={size}>
        <DialogHeader>
          <DialogTitle>
            {icon ? (
              <Inline as="span" gap={2} align="center">
                <Icon icon={icon} size="md" tone="primary" />
                {title}
              </Inline>
            ) : (
              title
            )}
          </DialogTitle>
        </DialogHeader>
        <PlanGateNotice {...body} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * The gate as the phone shows it, the Figma's video upsell (460:86937): the
 * sheet titled in its muted way, the reason at heading size with the
 * detail under it, the plans that include it two to a row, and No thanks
 * beside Upgrade as the sheet's own pill buttons.
 */
function PlanGateSheetContent({
  title,
  message,
  detail,
  error,
  primaryLabel,
  onPrimary,
  dismissLabel = 'Close',
  onDismiss,
  busy = false,
  plans,
  canUpgrade,
  askOwnerLabel = 'Ask your owner to upgrade.',
}: PlanGateNoticeProps & { title: React.ReactNode }) {
  const upgrades = canUpgrade !== false && plans?.length ? plans : null;
  return (
    <BottomSheetContent
      title={title}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="xl"
            pill
            onClick={onDismiss}
            disabled={busy}
          >
            {dismissLabel}
          </Button>
          {canUpgrade !== false && onPrimary ? (
            <Button
              type="button"
              size="xl"
              pill
              onClick={onPrimary}
              loading={busy}
            >
              {primaryLabel}
            </Button>
          ) : null}
        </>
      }
    >
      <Stack gap={6}>
        <Stack gap={4}>
          <Text variant="h2">{message}</Text>
          {detail ? (
            <Text variant="body-lg" tone="muted">
              {detail}
            </Text>
          ) : null}
          {canUpgrade === false ? (
            <Text variant="body-lg-strong">{askOwnerLabel}</Text>
          ) : null}
        </Stack>
        {upgrades ? (
          <Grid cols={upgrades.length > 1 ? 2 : 1} gap={2}>
            {upgrades.map((plan) => (
              <PlanCard
                key={plan.id}
                title={plan.name}
                price={
                  plan.price ? (
                    <Text variant="body-strong" as="span" numeric>
                      {plan.price}
                    </Text>
                  ) : undefined
                }
                features={plan.features}
                featuresTitle="Adds:"
                emphasis={plan.recommended}
                badge={plan.recommended ? 'Recommended' : undefined}
              />
            ))}
          </Grid>
        ) : null}
        {error ? <Alert tone="destructive">{error}</Alert> : null}
      </Stack>
    </BottomSheetContent>
  );
}
